import {Component, inject, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {NgbPagination} from '@ng-bootstrap/ng-bootstrap';
import {ChatHistoryService} from './chat-history.service';
import {ChatHistoryPageData} from '../../../../generated-model/models';
import {PageResponse} from '../../../shared/commons/page-response';
import {ThaiDateTimePipe} from '../../../shared/commons/thai-datetime.pipe';
import {PageTitleComponent} from '../../../shared/commons/page-title/page-title.component';
import {PagesizeSelectComponent} from '../../../shared/components/pagesize-select/pagesize-select.component';
import {downloadChatExport} from './chat-history-download';

type SortField = 'userName' | 'updateTime' | 'platform';

/**
 * หน้า "ประวัติแชท" — ยกมาจาก Joomla component com_rchat ของระบบเดิม (view=records)
 * คงพฤติกรรมเดิมไว้: ค้นหาจากชื่อคู่สนทนา, เรียงได้ 3 คอลัมน์, เลือกหลายรายการเพื่อส่งออก/ลบ
 */
@Component({
  selector: 'app-chat-history',
  standalone: true,
  imports: [FormsModule, NgbPagination, ThaiDateTimePipe, PageTitleComponent, PagesizeSelectComponent],
  templateUrl: './chat-history.component.html',
  styleUrl: './chat-history.component.scss',
})
export class ChatHistoryComponent implements OnInit {
  private readonly service = inject(ChatHistoryService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  protected term = signal<string>('');
  protected sortField = signal<SortField>('updateTime');
  protected sortDir = signal<'asc' | 'desc'>('desc');
  protected selectedIds = signal<Set<number>>(new Set<number>());
  protected message = signal<string | null>(null);
  protected loading = signal<boolean>(false);
  protected pageData = signal<PageResponse<ChatHistoryPageData>>({
    content: [],
    page: {size: 20, number: 0, totalElements: 0}
  } as PageResponse<ChatHistoryPageData>);

  /**
   * อาร์กิวเมนต์ของ thaidatetime ต้องเป็น object เดิมทุกครั้ง
   * ถ้าเขียน literal ไว้ใน template จะสร้าง object ใหม่ทุกรอบ change detection แล้ว pipe จะคำนวณใหม่ทุกครั้ง
   */
  readonly LONG_DATE = {month: 'long'} as const;

  ngOnInit(): void {
    // ระบบเดิมจำเงื่อนไขค้นหา/การเรียง/หน้าไว้ใน session ของ Joomla
    // ที่นี่เก็บไว้ใน query param แทน เพื่อให้กลับจากหน้ารายละเอียดแล้วอยู่ที่เดิม
    const query = this.route.snapshot.queryParamMap;
    this.term.set(query.get('term') ?? '');
    const sort = query.get('sort');
    if (sort === 'userName' || sort === 'updateTime' || sort === 'platform') {
      this.sortField.set(sort);
    }
    this.sortDir.set(query.get('dir') === 'asc' ? 'asc' : 'desc');
    const size = Number(query.get('size'));
    if (size > 0) {
      this.pageData.update(data => ({...data, page: {...data.page, size}}) as PageResponse<ChatHistoryPageData>);
    }
    this.search(Math.max(1, Number(query.get('page')) || 1));
  }

  search(page: number = 1): void {
    const params: Record<string, string | number> = {
      page: page - 1,
      size: this.pageData().page?.size ?? 20,
      sort: `${this.sortField()},${this.sortDir()}`
    };
    if (this.term().trim()) {
      params['term'] = this.term().trim();
    }
    this.syncUrl(page);

    this.loading.set(true);
    this.service.search(params).subscribe({
      next: (res) => {
        this.pageData.set(res);
        // ตัดรายการที่เลือกไว้แต่ไม่อยู่ในหน้านี้แล้วออก เพื่อไม่ให้ส่งออก/ลบผิดตัว
        const visible = new Set((res.content ?? []).map(row => row.id));
        this.selectedIds.update(ids => new Set([...ids].filter(id => visible.has(id))));
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.notify('ไม่สามารถโหลดประวัติการแชทได้');
      }
    });
  }

  reset(): void {
    this.term.set('');
    this.sortField.set('updateTime');
    this.sortDir.set('desc');
    this.selectedIds.set(new Set<number>());
    this.search();
  }

  /** คลิกคอลัมน์เดิมสลับทิศทาง คลิกคอลัมน์อื่นเริ่มที่ asc — เหมือนหัวตารางของระบบเดิม */
  sortBy(field: SortField): void {
    if (this.sortField() === field) {
      this.sortDir.set(this.sortDir() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortField.set(field);
      this.sortDir.set('asc');
    }
    this.search();
  }

  sortIcon(field: SortField): string {
    if (this.sortField() !== field) {
      return 'bi-arrow-down-up text-body-tertiary';
    }
    return this.sortDir() === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill';
  }

  onPageSizeChange(size: number): void {
    this.pageData.update(data => ({...data, page: {...data.page, size}}) as PageResponse<ChatHistoryPageData>);
    this.search();
  }

  openDetail(row: ChatHistoryPageData): void {
    // ส่งสถานะรายการติดไปด้วย ปุ่ม "ปิด" ของหน้ารายละเอียดจะพากลับมาที่เดิม
    void this.router.navigate(['/campaigns/chat-history', row.id], {queryParams: this.listState()});
  }

  /** สถานะของรายการที่ต้องจำไว้ */
  private listState(page?: number): Record<string, string | number> {
    const state: Record<string, string | number> = {
      page: page ?? (this.pageData().page?.number ?? 0) + 1,
      size: this.pageData().page?.size ?? 20,
      sort: this.sortField(),
      dir: this.sortDir()
    };
    if (this.term().trim()) {
      state['term'] = this.term().trim();
    }
    return state;
  }

  private syncUrl(page: number): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.listState(page),
      replaceUrl: true
    });
  }

  isSelected(id: number): boolean {
    return this.selectedIds().has(id);
  }

  toggleSelect(id: number): void {
    this.selectedIds.update(ids => {
      const next = new Set(ids);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  get allSelected(): boolean {
    const rows = this.pageData().content ?? [];
    return rows.length > 0 && rows.every(row => this.selectedIds().has(row.id));
  }

  toggleSelectAll(): void {
    const rows = this.pageData().content ?? [];
    this.selectedIds.set(this.allSelected ? new Set<number>() : new Set(rows.map(row => row.id)));
  }

  /** ส่งออกไฟล์: เลือกรายการเดียวได้ .txt เลือกหลายรายการได้ .zip (เหมือนระบบเดิม) */
  exportSelected(): void {
    const ids = [...this.selectedIds()];
    if (ids.length === 0) {
      this.notify('กรุณาเลือกประวัติการสนทนาที่ต้องการส่งออก');
      return;
    }
    this.service.export(ids).subscribe({
      next: (response) => downloadChatExport(response),
      error: () => this.notify('ส่งออกไฟล์ไม่สำเร็จ')
    });
  }

  deleteSelected(): void {
    const ids = [...this.selectedIds()];
    if (ids.length === 0) {
      this.notify('กรุณาเลือกประวัติการสนทนาที่ต้องการลบ');
      return;
    }
    if (!confirm('ยืนยันการลบข้อมูล?')) {
      return;
    }
    this.service.delete(ids).subscribe({
      next: () => {
        this.selectedIds.set(new Set<number>());
        this.notify('ลบข้อมูลการแชทเรียบร้อยแล้ว');
        // อยู่หน้าเดิมหลังลบ ไม่เด้งกลับหน้าแรก
        this.search((this.pageData().page?.number ?? 0) + 1);
      },
      error: () => this.notify('ลบข้อมูลไม่สำเร็จ')
    });
  }

  /** ระบบเดิมใช้ badge สีเข้มสำหรับ web และสีเขียวสำหรับ line */
  platformClass(platform: string | undefined): string {
    return platform === 'line' ? 'platform-badge--line' : 'platform-badge--web';
  }

  /** ระบบเดิมแสดง ucfirst($item->platform) เช่น "Web" / "Line" */
  platformLabel(platform: string | undefined): string {
    if (!platform) {
      return '-';
    }
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  }

  /**
   * updateTime มาจาก backend เป็น ISO-8601 instant (ลงท้ายด้วย Z)
   * ThaiDateTimePipe แยกสตริงเองด้วย split(/[^0-9]/) ซึ่งอ่านรูปแบบนี้ผิด
   * (ได้เวลา UTC มาแสดงเป็นเวลาไทย หรือกลายเป็น 00:00 เมื่อมีเศษวินาที)
   * จึงต้องแปลงเป็น Date ก่อน เพื่อให้ pipe ใช้สาขาที่แปลง timezone ถูกต้อง
   */
  toDate(value: Date | string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  rowNo(index: number): number {
    const page = this.pageData().page;
    return ((page?.size ?? 0) * (page?.number ?? 0)) + index + 1;
  }

  private notifyTimer: ReturnType<typeof setTimeout> | null = null;

  private notify(text: string): void {
    this.message.set(text);
    if (this.notifyTimer) {
      clearTimeout(this.notifyTimer);
    }
    this.notifyTimer = setTimeout(() => {
      this.message.set(null);
      this.notifyTimer = null;
    }, 3000);
  }
}
